/*!
 * Responsive Toolkit
 * Author:      Julio Lopez
 * Description: A helper for working with responsive breakpoints. Inspired by Maciej Gurban's Responsive Bootstrap
 *              toolkit. This Responsive Toolkit attempts to eliminate dependencies on libraries such as jQuery.
 *              Although, in its current form it does work better if lodash is available.
 * License:     MIT
 * Version:     {{VERSION}} ({{DATE}})
 * @see https://github.com/maciej-gurban/responsive-bootstrap-toolkit
 */
const ResponsiveToolKit = (
    function (window, document, undefined) {
        /**
         * Flag to help with our own throttle function.
         *
         * @type {boolean}
         */
        let throttlePause = false,
            breakpointEventHandler,
            currentBreakpoint;

        const checker = (possibleLodash) => {
            let isLodash = false;

            // we have a variable '_' that is either lodash or underscore
            if (possibleLodash !== undefined && possibleLodash !== null && typeof possibleLodash === 'function') {
                // Getting closer, both lodash and underscore have a 'forEach' feature
                if (possibleLodash.forEach !== undefined && possibleLodash.forEach !== null && typeof possibleLodash.forEach === 'function') {
                    // forEach to check for the existence of these lodash-only functions
                    const lodashFns = ['set', 'at', 'cloneDeep'];
                    // Assume we have lodash, but negate that assumption if even one of the functions are missing
                    isLodash = true;
                    lodashFns.forEach(
                        (fn) => {
                            isLodash = (typeof possibleLodash[fn] !== 'function') ? false : isLodash;
                        }
                    );
                }
            }

            return isLodash;
        },
            checkForLodash = () => {
                let hasLodash = checker(window._);

                if (hasLodash === true) {
                    return hasLodash;
                }

                hasLodash = checker(window.lodash);

                return hasLodash;
            },
            getLodash = () => {
                if (checkForLodash()) {
                    if (checker(window._)) {
                        return window._;
                    }

                    if (checker(window.lodash)) {
                        return window.lodash;
                    }
                }

                return undefined;
            }

        /** CONSTANTS **/
        /**
         * Flag to indicate if we have lodash on the page.
         *
         * @type {boolean}
         */
        const LODASH_EXISTS = checkForLodash(),

            /**
             * The lodash library if available.
             *
             * @type {function|undefined}
             */
            _ = LODASH_EXISTS ? getLodash() : undefined,

            /**
             * A list of valid operators for our `is()` function.
             *
             * @type {string[]}
             */
            VALID_OPERATORS = ['=', '>', '<', '>=', '<=', '!='],

            /**
             * Invalid operator error message.
             *
             * @type {string}
             */
            INVALID_OPERATOR_ERROR = 'Invalid operator passed to "ResponsiveToolKit.is()". Must be one of: ' + VALID_OPERATORS.join(', '),

            /**
             * Invalid breakpoint error message template. It is completed at runtime using the
             * currently available breakpoints.
             *
             * @type {string}
             */
            INVALID_BREAKPOINT_ERROR = 'Invalid breakpoint passed to "ResponsiveToolKit.is()". Must be one of: ',

            /**
             * Default frameworks and their breakpoints. Currently only Bootstrap is provided, others
             * can be added at runtime via the `use` function.
             *
             * @type {object}
             */
            DEFAULT_FRAMEWORKS = {
                bootstrap: {
                    xs: 0,
                    sm: 576,
                    md: 768,
                    lg: 992,
                    xl: 1200,
                    xxl: 1400
                }
            },
            /**
             * The name of the custom event indicating a breakpoint change
             * @type {string}
             */
            BREAKPOINT_CHANGED_EVENT = 'breakpointchanged';

        /** FUNCTIONS **/
        /**
         * Checks if supplied parameter is null or not.
         *
         * @param {*} v
         * @return {boolean}
         */
        const isNull = LODASH_EXISTS ? _.isNull : (v) => v === null,

            /**
             * Makes a deep clone of the supplied param.
             *
             * Please note that if lodash is not available a 'homebrewed' version of a cloneDeep
             * function is used. This could use some work as it is not perfect for every use-case.
             *
             * @param {*} v Any variable that needs to be cloned.
             * @return {*}
             */
            cloneDeep = LODASH_EXISTS ? _.cloneDeep : (v) => JSON.parse(JSON.stringify(v)),

            /**
             * Checks if an object has a particular property.
             *
             * Please note that if lodash is not available a 'homebrewed' version of a has
             * function is used. This could use some work as it is not perfect for every use-case.
             *
             * @param {object} o    Object to query for property.
             * @param {string} p    Property name or path to query.
             * @return {boolean}
             */
            has = LODASH_EXISTS ? _.has : (o, p) => {
                if (o === null) {
                    return false;
                }

                if (p.indexOf('.')) {
                    const pathParts = p.split('.');
                    let path = pathParts.shift(),
                        obj = cloneDeep(o);

                    while (path in obj) {
                        if (pathParts.length === 0) {
                            return true;
                        }
                        path = pathParts.shift();
                        obj = cloneDeep(obj[path]);
                    }

                    return false
                } else {
                    return p in o;
                }
            },

            /**
             * Throttles the running of a function.
             *
             * Please note that if lodash is not available a 'homebrewed' version of a throttle
             * function is used. It may not be the best solution, but it's better than nothing.
             *
             * @param {function} callback   Function that will be throttled.
             * @param {number} time         Throttle time limit.
             * @returns {function}          A throttled function
             */
            throttle = LODASH_EXISTS ? _.throttle : (callback, time) => {
                return () => {
                    if (throttlePause) {
                        return;
                    }

                    throttlePause = true;
                    const throttleCb = () => {
                        callback();
                        throttlePause = false;
                    }
                    setTimeout(throttleCb, time);
                }
            },

            /**
             * Gets the width of the viewport.
             *
             * @returns {number}    The current width, in pixels, of the viewport
             */
            getViewportWidth = () => window.innerWidth,

            /**
             * Gets the next biggest breakpoint after the provided breakpoint from the list of supplied breakpoints.
             *
             * @param {string} breakpoint   The name of the breakpoint we start our search at.
             * @param {object} breakpoints  The list of breakpoints we are searching through.
             * @returns {number}            The minimum width, in pixels, of the breakpoint that is the next biggest
             */
            getNextBiggestBreakpoint = (breakpoint, breakpoints) => {
                if (!has(breakpoints, breakpoint)) {
                    const bpKeys = Object.keys(breakpoints),
                        errMsg = INVALID_BREAKPOINT_ERROR + bpKeys.join(', ');
                    throw new Error(errMsg);
                }

                let bPts = [],
                    bPt;

                for (bPt in breakpoints) {
                    if (breakpoints[bPt] > breakpoints[breakpoint]) {
                        bPts.push(breakpoints[bPt]);
                    }
                }

                if (bPts.length === 0) {
                    return breakpoints[breakpoint];
                }

                bPts.sort((a, b) => a - b);

                return bPts.shift();
            },

            /**
             * Gets the next smallest breakpoint after the provided breakpoint from the list of supplied breakpoints.
             *
             * @param {string} breakpoint   The name of the breakpoint we start our search at.
             * @param {object} breakpoints  The list of breakpoints we are searching through.
             * @returns {number}            The minimum width, in pixels, of the breakpoint that is the next smallest
             */
            getNextSmallestBreakpoint = (breakpoint, breakpoints) => {
                if (!has(breakpoints, breakpoint)) {
                    const bpKeys = Object.keys(breakpoints),
                        errMsg = INVALID_BREAKPOINT_ERROR + bpKeys.join(', ');
                    throw new Error(errMsg);
                }

                let bPts = [],
                    bPt;

                for (bPt in breakpoints) {
                    if (breakpoints[bPt] < breakpoints[breakpoint]) {
                        bPts.push(breakpoints[bPt]);
                    }
                }

                if (bPts.length === 0) {
                    return breakpoints[breakpoint];
                }

                bPts.sort((a, b) => a - b);

                return bPts.pop();
            },

            dispatchBreakpointEvents = () => {
                const breakpointNow = window.ResponsiveToolKit.current();

                if (breakpointNow !== currentBreakpoint) {
                    const opts = {
                        bubbles: true,
                        cancelable: true,
                        detail: {
                            from: currentBreakpoint,
                            to: breakpointNow
                        }
                    };
                    const bpEvent = new CustomEvent(BREAKPOINT_CHANGED_EVENT, opts);
                    window.ResponsiveToolKit.dispatchEvent(bpEvent);
                }

                currentBreakpoint = breakpointNow;
            },
            defaultBreakpoints = cloneDeep(DEFAULT_FRAMEWORKS['bootstrap']);

        /**
         * Public object returned after instantiation.
         */
        class ResponsiveToolKit extends EventTarget {
            constructor(
                interval = 250,
                framework = 'bootstrap',
                breakpoints = defaultBreakpoints,
                dispatchesBreakpointEvents = true
            ) {
                super();

                /**
                 * Default throttle time in milliseconds.
                 *
                 * @type {number}
                 */
                this.interval = interval;

                /**
                 * The name of the framework in use.
                 *
                 * @type {string|null}
                 */
                this.framework = framework;

                /**
                 * An object containing all available breakpoints for the currently selected framework.
                 *
                 * @type {object|null}
                 */
                this.breakpoints = breakpoints;

                /**
                 * A flag to indicate whether it should dispatch events when the breakpoint changes
                 *
                 * @type {boolean}
                 */
                this.dispatchesBreakpointEvents = dispatchesBreakpointEvents;

                /**
                 * Name of the event triggered when a breakpoint changes
                 */
                this.BREAKPOINT_CHANGED_EVENT = BREAKPOINT_CHANGED_EVENT;

                if (dispatchesBreakpointEvents) {
                    this.enableBreakpointEvents();
                }
            }

            /**
             * Checks if the current viewport is showing the breakpoint in question. Optional operator can
             * be passed to change the nature of the query.
             *
             * @param {string} breakpoint   The breakpoint we are querying.
             * @param {string} operator     An operator to modify the query. Available operators are
             *                              '=', '>', '<', '>=', '<=' and '!='. Default is '='. Optional.
             * @return {boolean}            True if the breakpoint query is successful, false otherwise.
             */
            is(breakpoint, operator = '=') {
                if (!VALID_OPERATORS.includes(operator)) {
                    throw new Error(INVALID_OPERATOR_ERROR);
                }

                if (!has(this.breakpoints, breakpoint)) {
                    const bpKeys = Object.keys(this.breakpoints),
                        errMsg = INVALID_BREAKPOINT_ERROR + bpKeys.join(', ');
                    throw new Error(errMsg);
                }

                const width = getViewportWidth(),
                    nextBiggest = getNextBiggestBreakpoint(breakpoint, this.breakpoints)

                switch (true) {
                    case operator === '=':
                        if (this.breakpoints[breakpoint] === nextBiggest) {
                            return width >= this.breakpoints[breakpoint];
                        }

                        return width >= this.breakpoints[breakpoint] && width < nextBiggest;
                    case operator === '>':
                        return width >= nextBiggest;
                    case operator === '<':
                        return width < this.breakpoints[breakpoint];
                    case operator === '>=':
                        return width >= this.breakpoints[breakpoint];
                    case operator === '<=':
                        return width < nextBiggest;
                    case operator === '!=':
                        if (this.breakpoints[breakpoint] === nextBiggest) {
                            return width < this.breakpoints[breakpoint];
                        }

                        return width < this.breakpoints[breakpoint] || width >= nextBiggest;
                }
            }

            /**
             * Returns the current viewport width
             *
             * @return {number} The width of the viewport at the time the function was called.
             */
            width() {
                return getViewportWidth();
            }

            /**
             * Defines which framework to use. Will search default frameworks, and use one if found. If framework
             * is not a default framework, will use the supplied breakpoints.
             *
             * @param {string} framework    Name of the framework.
             * @param {object} breakpoints  Object of breakpoints to use. Optional.
             */
            use(framework, breakpoints = {}) {
                framework = framework.toLowerCase();

                this.framework = framework;
                if (has(DEFAULT_FRAMEWORKS, framework)) {
                    this.breakpoints = cloneDeep(DEFAULT_FRAMEWORKS[framework]);
                } else if (Object.keys(breakpoints).length > 0) {
                    this.breakpoints = cloneDeep(breakpoints);
                } else {
                    this.breakpoints = cloneDeep(DEFAULT_FRAMEWORKS['bootstrap'])
                }
            }

            /**
             * Returns the current breakpoint for the viewport.
             *
             * @return {string|null}    The name of the current breakpoint
             */
            current() {
                let current = null,
                    bPt;

                for (bPt in this.breakpoints) {
                    if (this.is(bPt)) {
                        current = bPt;
                        break;
                    }
                }

                return current;
            }

            /**
             * Returns the next biggest breakpoint, or the current breakpoint if there are none bigger.
             *
             * @return {string|null}    The name of the next biggest breakpoint, or null if no
             *                          bigger breakpoints exist.
             */
            nextBreakpointUp() {
                const nextUp = getNextBiggestBreakpoint(this.current(), this.breakpoints),
                    current = this.breakpoints[this.current()];

                let nextBtp = null,
                    bPt;

                if (nextUp === current) {
                    return this.current();
                }

                for (bPt in this.breakpoints) {
                    if (this.breakpoints[bPt] < current || bPt === this.current()) {
                        continue;
                    }

                    if (this.breakpoints[bPt] === nextUp) {
                        nextBtp = bPt;
                        break;
                    }
                }

                return nextBtp;
            }

            /**
             * Returns the next biggest breakpoint, or the current breakpoint if there are none bigger.
             *
             * @return {string|null}    The name of the next smallest breakpoint, or null if no
             *                          smaller breakpoints exist.
             */
            nextBreakpointDown() {
                const nextDown = getNextSmallestBreakpoint(this.current(), this.breakpoints),
                    current = this.breakpoints[this.current()];

                let nextBtp = null,
                    bPt;

                if (nextDown === current) {
                    return this.current();
                }

                for (bPt in this.breakpoints) {
                    if (this.breakpoints[bPt] > current || bPt === this.current()) {
                        continue;
                    }

                    if (this.breakpoints[bPt] === nextDown) {
                        nextBtp = bPt;
                        break;
                    }
                }

                return nextBtp;
            }

            /**
             * Attaches a throttled event handler to the viewport's resize event. Returns a reference to the
             * throttled function in case you want to remove it later with `ignore()`.
             *
             * @param fn                    Function to use as the event handler.
             * @param t                     Time interval for the throttling.
             * @return {(function(): void)} The throttled event handler
             */
            observe(fn, t = this.interval) {
                const cb = throttle(fn, t);
                window.addEventListener('resize', cb);
                return cb;
            }

            /**
             * Removes the supplied throttled event handler from the viewport's resize event.
             * @param cb        A throttled function returned from `observe()`.
             *
             * @return {void}
             */
            ignore(cb) {
                window.removeEventListener('resize', cb);
            }

            /**
             * Enables the dispatching of the 'Breakpoint Changed' custom event
             */
            enableBreakpointEvents() {
                this.dispatchesBreakpointEvents = true;
                currentBreakpoint = this.current();
                breakpointEventHandler = this.observe(dispatchBreakpointEvents, 200);
            }

            /**
             * Disables the dispatching of the 'Breakpoint Changed' custom event
             */
            disableBreakpointEvents() {
                this.dispatchesBreakpointEvents = true;
                this.ignore(breakpointEventHandler);
            }
        }

        return new ResponsiveToolKit();
    }
)(window, document);
window.ResponsiveToolKit = ResponsiveToolKit;